// fileMime.js — normalize browser MIME + filename for uploads / PDF detection

/** Canonical PDF MIME. */
export const PDF_MIME_TYPE = 'application/pdf';

/**
 * Vendor / legacy MIME strings browsers and OSes sometimes report for PDFs.
 * Includes Chrome's built-in viewer type ("Chrome PDF").
 */
const PDF_MIME_ALIASES = new Set([
  'application/pdf',
  'application/x-pdf',
  'application/acrobat',
  'application/vnd.pdf',
  'application/x-google-chrome-pdf',
  'applications/vnd.pdf',
  'text/pdf',
  'text/x-pdf',
]);

/** MIME values that mean "unknown" — fall back to filename extension. */
const GENERIC_MIME_TYPES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'application/force-download',
  'application/download',
]);

const DOCUMENT_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'docm',
  'dot',
  'dotx',
  'rtf',
  'odt',
  'pages',
  'xls',
  'xlsx',
  'xlsm',
  'xlsb',
  'csv',
  'tsv',
  'ods',
  'numbers',
  'ppt',
  'pptx',
  'pps',
  'ppsx',
  'odp',
  'key',
  'txt',
  'md',
  'markdown',
]);

const PHOTO_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
  'avif',
  'heic',
  'heif',
  'tif',
  'tiff',
]);

const VIDEO_EXTENSIONS = new Set([
  'mp4',
  'mov',
  'm4v',
  'webm',
  'avi',
  'mkv',
  'mpeg',
  'mpg',
]);

/**
 * @param {string} [fileName]
 * @returns {string} Lowercase extension without dot, or ''.
 */
export function getFileNameExtension(fileName = '') {
  const name = String(fileName || '').trim();
  if (!name.includes('.')) return '';
  const ext = name.split('.').pop() || '';
  return ext.toLowerCase();
}

/**
 * @param {string} [mimeType]
 * @returns {boolean}
 */
export function isPdfMimeType(mimeType = '') {
  const mime = String(mimeType || '').trim().toLowerCase();
  if (!mime) return false;
  if (PDF_MIME_ALIASES.has(mime)) return true;
  // Chrome / plugin strings like application/x-google-chrome-pdf
  if (mime.includes('pdf') && (mime.startsWith('application/') || mime.startsWith('text/'))) {
    return true;
  }
  return false;
}

/**
 * @param {string} [mimeType]
 * @returns {boolean}
 */
export function isGenericMimeType(mimeType = '') {
  return GENERIC_MIME_TYPES.has(String(mimeType || '').trim().toLowerCase());
}

/**
 * Normalize a browser-reported MIME using filename when needed.
 * Chrome "Save as PDF" / Finder "Chrome PDF Document" files often arrive as
 * application/x-google-chrome-pdf, empty type, or octet-stream despite a .pdf name.
 *
 * @param {string} [mimeType]
 * @param {string} [fileName]
 * @returns {string} Normalized MIME (may be empty when still unknown).
 */
export function normalizeFileMimeType(mimeType = '', fileName = '') {
  const mime = String(mimeType || '').trim().toLowerCase();
  const extension = getFileNameExtension(fileName);

  if (isPdfMimeType(mime) || (isGenericMimeType(mime) && extension === 'pdf')) {
    return PDF_MIME_TYPE;
  }

  if (mime) return mime;

  if (PHOTO_EXTENSIONS.has(extension)) {
    if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
    if (extension === 'tif' || extension === 'tiff') return 'image/tiff';
    return `image/${extension}`;
  }

  if (VIDEO_EXTENSIONS.has(extension)) {
    if (extension === 'mov') return 'video/quicktime';
    if (extension === 'mpg' || extension === 'mpeg') return 'video/mpeg';
    return `video/${extension}`;
  }

  if (extension === 'pdf') return PDF_MIME_TYPE;
  if (extension === 'txt') return 'text/plain';
  if (extension === 'csv') return 'text/csv';
  if (extension === 'md' || extension === 'markdown') return 'text/markdown';
  if (extension === 'rtf') return 'application/rtf';
  if (extension === 'doc') return 'application/msword';
  if (extension === 'docx') {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }

  return '';
}

/**
 * @param {string} [fileName]
 * @returns {'PHOTO'|'VIDEO'|'DOCUMENT'|null}
 */
export function detectNodeTypeFromExtension(fileName = '') {
  const extension = getFileNameExtension(fileName);
  if (!extension) return null;
  if (PHOTO_EXTENSIONS.has(extension)) return 'PHOTO';
  if (VIDEO_EXTENSIONS.has(extension)) return 'VIDEO';
  if (DOCUMENT_EXTENSIONS.has(extension)) return 'DOCUMENT';
  return null;
}

/**
 * Whether a document node should use the native PDF viewer / PDF extract path.
 *
 * @param {object} [node]
 * @returns {boolean}
 */
export function isPdfDocumentNode(node = {}) {
  const mimeType = String(
    node?.data_document?.mimeType
    || node?.data_document?.contentType
    || node?.mimeType
    || node?.contentType
    || ''
  );
  if (isPdfMimeType(mimeType)) return true;

  const name = String(
    node?.data_document?.filename
    || node?.data_document?.fileName
    || node?.fileName
    || node?.title
    || ''
  );
  if (/\.pdf$/i.test(name)) return true;

  const extension = String(node?.data_document?.extension || '').toLowerCase();
  return extension === 'pdf';
}
